<template>
    <div>
        <Form
            :countries="countries"
            :states="states"
            :phone-country-code-placeholder="phoneCountryCodePlaceholder"
            :company-sanities="companySanities"
            :brands="brands"
            :titles="titles"
            :customer-types="customerTypes"
            :payment-terms="paymentTerms"
            :payment-methods="paymentMethods"
            :tax-exemptions="taxExemptions"
            :billing-types="billingTypes"
            :customer="customer"
            :customer-segments="customerSegments"
            :invoice-aggregations="invoiceAggregations"
            :invoice-delivery-types="invoiceDeliveryTypes"
            :selected-invoice-delivery-type="selectedInvoiceDeliveryType"
            :electronic-invoicing-types="electronicInvoicingTypes"
            :errors="errors"
            :use-herold-location-id-on-customer="useHeroldLocationIdOnCustomer"
            :customer-location="customerLocation"
            :ssn-business-id="ssnBusinessId"
            :ssn-check-on-edit="true"
            :legal-forms="legalForms"
            :original-brands="originalBrands"
            :communication-subscriptions="communicationSubscriptions"
            :active-communication-subscriptions="activeCommunicationSubscriptions"
            :is-finance-system-netsuite="isFinanceSystemNetsuite"
            @form-submitted="submitData"
            @open-vat-id-check="openCheckVatIdModal"
        />

        <CriticalActions
            v-if="customer.status === 'active' || customer.status === 'fake' || customer.status === 'inactive' || customer.status === 'restricted'"
            :customer="customer"
            :has-active-contracts="hasActiveContracts"
            @fake-customer="fakeCustomerModal"
            @reactivate-customer="reactivateCustomerModal"
            @deactivate-customer="deactivateCustomerModal"
            @restricted-customer="restrictedCustomerModal"
        />

        <DModal
            v-model="activityLogComment"
            :show="showCriticalActionsModal || modalHasErrors"
            :title="criticalActionModalTitle"
            :message="criticalActionModalDescription"
            is-confirm
            is-error
            is-awaiting-validation
            show-change-reason-input
            @confirm="confirmCriticalAction"
            @close="closeCriticalModal"
        />

        <EditWithInvalidDataModal
            :show-modal="showEditWithInvalidDataModal"
            :invalid-fields="formData.ignoreProperties"
            @close-modal="closeEditWithInvalidDataModal"
            @confirm-modal="confirmEdit"
        />
    </div>
</template>

<script>
import CustomerBase from '~@/Pages/Customers/Base'
import App from '~@/App.vue'

import Form from '~@/Components/Customers/Customers/Form'
import CriticalActions from '~@/Components/Customers/Customers/CriticalActions'
import EditWithInvalidDataModal from '~@/Components/Customers/Customers/Modals/EditWithInvalidDataModal'

export default {
    name: 'CustomerEdit',
    components: {
        CriticalActions,
        Form,
        EditWithInvalidDataModal
    },
    layout: [App, CustomerBase],
    props: {
        errors: {
            type: Object,
            default: () => { return {} }
        },
        countries: {
            type: Array,
            default: () => { return [] }
        },
        states: {
            type: Object,
            default: () => { return {} }
        },
        phoneCountryCodePlaceholder: {
            type: String,
            default: '+49'
        },
        brands: {
            type: Array,
            default: () => { return [] }
        },
        titles: {
            type: Array,
            default: () => { return [] }
        },
        customerTypes: {
            type: Array,
            default: () => { return [] }
        },
        paymentTerms: {
            type: Array,
            default: () => { return [] }
        },
        paymentMethods: {
            type: Array,
            default: () => { return [] }
        },
        taxExemptions: {
            type: Array,
            default: () => { return [] }
        },
        billingTypes: {
            type: Array,
            default: () => { return [] }
        },
        customer: {
            type: Object,
            default: () => { return {} }
        },
        companySanities: {
            type: Array,
            default: () => { return [] }
        },
        stickyNotes: {
            type: Array,
            default: () => { return [] }
        },
        customerSegments: {
            type: Array,
            default: () => { return [] }
        },
        hasActiveContracts: {
            type: Boolean,
            default: () => { return true }
        },
        invoiceAggregations: {
            type: Array,
            default: () => { return [] }
        },
        invoiceDeliveryTypes: {
            type: Array,
            default: () => { return [] }
        },
        selectedInvoiceDeliveryType: {
            type: String,
            default: ''
        },
        electronicInvoicingTypes: {
            type: Array,
            default: () => { return [] }
        },
        useHeroldLocationIdOnCustomer: {
            type: Boolean,
            default: () => { return false }
        },
        ssnBusinessId: {
            type: Boolean,
            default: () => { return false }
        },
        customerLocation: {
            type: String,
            default: ''
        },
        legalForms: {
            type: Array,
            default: () => { return [] }
        },
        originalBrands: {
            type: Array,
            default: () => { return [] }
        },
        communicationSubscriptions: {
            type: Array,
            default: () => { return [] }
        },
        activeCommunicationSubscriptions: {
            type: Array,
            default: () => { return [] }
        },
        isFinanceSystemNetsuite: {
            type: Boolean,
            default: false
        },
    },
    data () {
        return {
            showCriticalActionsModal: false,
            criticalActionModalTitle: '',
            criticalActionModalDescription: '',
            activityLogComment: '',
            criticalActionCallback: () => {},
            showEditWithInvalidDataModal: false,
            formData: {},
            modalHasErrors: false
        }
    },
    methods: {
        submitData (event) {
            event.form.put(this.$route('customers.customers.update', this.customer.id), {
                onSuccess: () => {
                },
                onError: (errors) => {
                    let preventValidationIgnore = false
                    // if error is for one of the fields that we changed, then don't allow validation ignore
                    event.updatedProperties.forEach(function (property) {
                        if (property in errors) {
                            preventValidationIgnore = true
                        }
                    })

                    if (preventValidationIgnore === true) {
                        return
                    }

                    // get all error keys
                    const errorsKeys = Object.keys(errors)

                    if (errorsKeys.length > 0) {
                        errorsKeys.forEach(item => delete errors[item])
                    }

                    this.showEditWithInvalidDataModal = true
                    this.formData = event.form
                    this.formData.ignoreProperties = errorsKeys
                }
            })
        },

        fakeCustomerModal () {
            this.criticalActionModalTitle = this.__('customers.customers.critical_actions.fake.modal.title')
            this.criticalActionModalDescription = this.__('customers.customers.critical_actions.fake.modal.description')
            this.showCriticalActionsModal = true

            this.criticalActionCallback = () => this.$inertia.post(this.$route('customers.customers.mark-as-fake', { customer: this.customer.id }), {
                activity_log_comment: this.activityLogComment
            }, {
                onSuccess: () => {
                    this.showCriticalActionsModal = this.modalHasErrors = false
                },
                onError: () => {
                    this.modalHasErrors = true
                }
            })
        },
        reactivateCustomerModal () {
            this.criticalActionModalTitle = this.__('customers.customers.critical_actions.reactivate.modal.title')
            this.criticalActionModalDescription = this.__('customers.customers.critical_actions.reactivate.modal.description')
            this.showCriticalActionsModal = true

            this.criticalActionCallback = () => this.$inertia.post(this.$route('customers.customers.reactivate', { customer: this.customer.id }), {
                activity_log_comment: this.activityLogComment
            },
            {
                onSuccess: () => {
                    this.showCriticalActionsModal = this.modalHasErrors = false
                },
                onError: () => {
                    this.modalHasErrors = true
                }
            })
        },
        deactivateCustomerModal () {
            this.criticalActionModalTitle = this.__('customers.customers.critical_actions.deactivate.modal.title')
            this.criticalActionModalDescription = this.__('customers.customers.critical_actions.deactivate.modal.description')
            this.showCriticalActionsModal = true

            this.criticalActionCallback = () => this.$inertia.post(this.$route('customers.customers.deactivate', { customer: this.customer.id }), {
                activity_log_comment: this.activityLogComment
            },
            {
                onSuccess: () => {
                    this.showCriticalActionsModal = this.modalHasErrors = false
                },
                onError: () => {
                    this.modalHasErrors = true
                }
            })
        },
        restrictedCustomerModal () {
            this.criticalActionModalTitle = this.__('customers.customers.critical_actions.restricted.modal.title')
            this.criticalActionModalDescription = this.__('customers.customers.critical_actions.restricted.modal.description')
            this.showCriticalActionsModal = true

            this.criticalActionCallback = () => this.$inertia.post(this.$route('customers.customers.gdpr-restriction', { customer: this.customer.id }), {
                activity_log_comment: this.activityLogComment,
            },
            {
                onSuccess: () => {
                    this.showCriticalActionsModal = this.modalHasErrors = false
                },
                onError: () => {
                    this.modalHasErrors = true
                }
            })
        },
        confirmCriticalAction () {
            this.criticalActionCallback()
        },
        confirmEdit () {
            this.formData.put(this.$route('customers.customers.update', this.customer.id))
        },
        openCheckVatIdModal () {
            this.showVatIdCheckModal = true
        },
        closeCriticalModal () {
            this.showCriticalActionsModal = this.modalHasErrors = false
            this.activityLogComment = ''
        },
        closeEditWithInvalidDataModal () {
            this.showEditWithInvalidDataModal = false
            this.formData.ignoreProperties = []
        }
    }
}
</script>
